// src/components/authorizations/KlaviyoConnectionForm.tsx
//
// Klaviyo auth-create wizard (plan 2026-06-28-add-klaviyo-source §A7).
// Single-form flow mirroring Brevo: private API key + Test connection + name.
// Phase 1 (source-only) expects a READ-ONLY private key — the copy below
// tells the customer exactly that; the same authorization's key is rotated
// to read/write at the Phase-2 destination cutover.
//
// Rendered inline in the setup wizard's modal (a drawer launched from a modal
// sits under the modal's overlay and focus trap, so its fields cannot be
// used), and in a drawer on the Authorizations page.
import { useState } from "react";
import {
  Alert,
  Button,
  Group,
  PasswordInput,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { IconAlertTriangle, IconCheck } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import {
  testKlaviyoCredentials,
  createKlaviyoAuthorization,
} from "@/api/authorizations";

type Props = {
  /** Called after the connection was created. */
  onCreated: () => void;
  /** Shown as a Cancel button when set. */
  onCancel?: () => void;
};

export function KlaviyoConnectionForm({ onCreated, onCancel }: Props) {
  const [name, setName] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{
    klaviyo_account_id: string;
    organization_name: string;
    test_account: boolean;
  } | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleTest = async () => {
    if (!apiKey.trim()) {
      notifications.show({ color: "red", message: "API key is required" });
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const result = await testKlaviyoCredentials(apiKey.trim());
      setTestResult(result);
      if (!name.trim() && result.organization_name) {
        setName(result.organization_name);
      }
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Connection test failed";
      notifications.show({ color: "red", title: "Test failed", message });
    } finally {
      setTesting(false);
    }
  };

  const canSubmit = !!testResult && !!name.trim() && !!apiKey.trim();

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await createKlaviyoAuthorization({
        name: name.trim(),
        apiKey: apiKey.trim(),
      });
      notifications.show({
        color: "green",
        message: "Klaviyo authorization created",
      });
      onCreated();
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Failed to create authorization";
      notifications.show({ color: "red", title: "Error", message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Stack gap="md">
      <Text size="sm" c="dimmed">
        Create a <strong>read-only private API key</strong> in Klaviyo under
        Settings &rsaquo; Account &rsaquo; API Keys, with at least the
        Accounts, Events, Profiles, Campaigns and Flows read scopes (add
        Lists and Segments read to import your audiences), and paste it here.
      </Text>

      <PasswordInput
        label="Private API key"
        placeholder="pk_..."
        value={apiKey}
        onChange={(e) => {
          setApiKey(e.currentTarget.value);
          setTestResult(null);
        }}
        required
      />

      <Button
        variant="light"
        onClick={handleTest}
        loading={testing}
        disabled={!apiKey.trim()}
      >
        Test connection
      </Button>

      {testResult && (
        <Alert icon={<IconCheck size={16} />} color="green" variant="light">
          Connected to{" "}
          <strong>{testResult.organization_name || "your Klaviyo account"}</strong>{" "}
          ({testResult.klaviyo_account_id}).
        </Alert>
      )}

      {testResult?.test_account && (
        <Alert
          icon={<IconAlertTriangle size={16} />}
          color="yellow"
          variant="light"
        >
          This looks like a Klaviyo test account. That is fine for trying
          things out, but remember to connect your production account later.
        </Alert>
      )}

      <TextInput
        label="Authorization name"
        placeholder="My Klaviyo account"
        value={name}
        onChange={(e) => setName(e.currentTarget.value)}
        required
      />

      <Group justify="flex-end" mt="md">
        {onCancel && (
          <Button variant="default" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button
          onClick={handleSubmit}
          loading={submitting}
          disabled={!canSubmit}
        >
          Create authorization
        </Button>
      </Group>
    </Stack>
  );
}
